import classNames from "classnames";
import { FC, useMemo } from "react";

import { ReactComponent as EmptyIcon } from "assets/images/common/gallery.svg";
import { ReactComponent as LocationIcon } from "assets/images/common/location.svg";
import { PortfolioItem } from "entities/portfolios";

type PropTypes = {
  data?: PortfolioItem;
  className?: string;
  onClick?: () => void;
  isShowCompanyName?: boolean;
};

const formatPortfolioAddress = (parts: (string | null | undefined)[]): string =>
  parts
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(", ");

const PortfolioCard: FC<PropTypes> = ({ data, className, onClick, isShowCompanyName = false }) => {
  const {
    name,
    images,
    manager,
    compliance_score,
    description,
    asset_count,
    company_name,
    company_city,
    active_incidents,
    city,
    area,
    building_name,
    unit_number,
    street,
  } = data || {};

  const imageUrl = useMemo(() => images?.[0]?.url, [images]);

  const formattedAddress = useMemo(
    () => formatPortfolioAddress([company_city?.label, city, area, building_name, unit_number, street]),
    [company_city?.label, city, area, building_name, unit_number, street],
  );

  const complianceLevel = useMemo(() => {
    if (typeof compliance_score !== "number") return "unknown";
    if (compliance_score >= 90) return "stable";
    if (compliance_score >= 70) return "attention";
    return "critical";
  }, [compliance_score]);

  const incidentsLevel = useMemo(() => {
    if (typeof compliance_score !== "number") return "unknown";
    return active_incidents ? "critical" : "stable";
  }, [compliance_score, active_incidents]);

  const incidentsCount = active_incidents ?? 0;

  return (
    <div
      onClick={onClick}
      className={classNames("portfolio-card", className, {
        "portfolio-card--editable": !!onClick,
      })}
    >
      <div className={"portfolio-card__image"}>
        {imageUrl && <img className={"portfolio-card__image-img"} src={imageUrl} alt={name} />}
        {!imageUrl && (
          <div className={"portfolio-card__image-img"}>
            <EmptyIcon />
          </div>
        )}

        <div className={"portfolio-card__status"}>
          <div className={classNames("portfolio-card__image-badges", `portfolio-card__status-${complianceLevel}`)}>
            {typeof compliance_score === "number" ? `Compliance: ${compliance_score}%` : "-"}
          </div>

          <div
            className={classNames("portfolio-card__image-badges", `portfolio-card__status-${incidentsLevel}-light`)}
          >
            {incidentsCount} active incident{incidentsCount !== 1 ? "s" : ""}
          </div>
        </div>
      </div>

      <div className={"portfolio-card__content"}>
        <div className={"portfolio-card__title"}>
          <p>{name}</p>
          {isShowCompanyName && <span>{company_name}</span>}
        </div>

        <div className={"portfolio-card__description"}>
          <p>{description}</p>

          <div>
            <LocationIcon />
            <span>{formattedAddress}</span>
          </div>
        </div>

        <div className={"portfolio-card__assets"}>
          <span>Assets</span>
          <span>{asset_count || 0}</span>
        </div>

        <div className={"portfolio-card__manager"}>
          {manager?.url && <img src={manager?.url} alt={manager?.full_name} />}
          {!manager?.url && <div className={"portfolio-card__manager-avatar"}>{manager?.full_name?.charAt(0)}</div>}

          <div className={"portfolio-card__manager-info"}>
            <p>{manager?.full_name}</p>
            <p>Portfolio manager</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioCard;
